import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { CrmProvider } from "./types";
import { cn } from "@/lib/utils";

type CrmOption = {
  id: CrmProvider;
  name: string;
};

type CrmSelectorProps = {
  options: CrmOption[];
  selectedOption?: CrmProvider | null;
  onSelect: (option: CrmProvider | null) => void;
  placeholder?: string;
  width?: string | number;
};

const CrmSelector = ({
  options,
  selectedOption,
  onSelect,
  placeholder = "Selecciona un CRM",
}: CrmSelectorProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSelectedOption, setInternalSelectedOption] = useState<CrmProvider | null>(
    selectedOption || null
  );
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInternalSelectedOption(selectedOption || null);
  }, [selectedOption]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleSelect = (option: CrmProvider) => {
    setInternalSelectedOption(option);
    onSelect(option);
    setIsOpen(false);
  };

  const handleDeselect = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInternalSelectedOption(null);
    onSelect(null);
  };

  const selectedOptionData = options.find((opt) => opt.id === internalSelectedOption);

  return (
    <div className="relative w-full" ref={selectRef} onKeyDown={handleKeyDown}>
      {/* Selector Box */}
      <div
        className="flex w-full cursor-pointer items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 hover:border-gray-300 hover:bg-white focus:border-purple-500 focus:bg-white"
        onClick={toggleDropdown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        role="combobox"
      >
        <p
          className={cn(
            "text-sm",
            internalSelectedOption ? "font-medium text-gray-700" : "font-normal text-gray-500"
          )}
        >
          {selectedOptionData?.name || placeholder}
        </p>
        <div className="flex flex-row gap-2">
          {internalSelectedOption && (
            <X
              className="size-4 cursor-pointer text-gray-500"
              onClick={handleDeselect}
            />
          )}
          {isOpen ? (
            <ChevronUp className="size-4 text-gray-500" />
          ) : (
            <ChevronDown className="size-4 text-gray-500" />
          )}
        </div>
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div
          className="absolute left-0 top-full z-10 mt-2 max-h-[200px] w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg"
          role="listbox"
        >
          <div className="flex flex-col">
            {options.map((option) => (
              <div
                key={option.id}
                className="flex cursor-pointer items-center p-3 hover:bg-gray-100"
                onClick={() => handleSelect(option.id)}
                role="option"
                aria-selected={option.id === internalSelectedOption}
              >
                <p className="text-sm text-gray-700">
                  {option.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmSelector;
